import React from 'react';
import { Rocket, Sparkles, Globe, Layout, Image as ImageIcon, ArrowRight, Zap } from 'lucide-react';

const LandingPage = ({ onStart, onShowPrivacy, onShowTerms, onShowContact }) => {
  return (
    <div className="min-h-screen bg-black text-white font-body selection:bg-brand-orange/40">
      {/* Navigation */}
      <nav className="border-b border-brand-gray-800 bg-black/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-brand-orange p-2.5 rounded-lg">
              <Rocket className="w-6 h-6 text-black" />
            </div>
            <span className="text-xl font-headline uppercase tracking-wider text-white">
              FlowGen.social
            </span>
          </div>
          <button 
            onClick={onStart}
            data-testid="launch-app-btn"
            className="btn-secondary text-sm"
          >
            Launch App
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 pb-32 lg:pt-32 lg:pb-40">
        {/* Orange glow effect */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-brand-orange/20 rounded-full blur-[150px] -z-10"></div>
        
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-bold uppercase tracking-wider mb-8">
            <Zap className="w-3 h-3" /> AI-Powered Content Generation
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline uppercase tracking-wider text-white mb-8 leading-tight">
            Stop Re-Explaining Yourself to AI
          </h1>
          
          <p className="text-base lg:text-lg text-brand-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Generate tailored content calendars, create stunning AI visuals, and research real-time trends — all with an AI that remembers your brand.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={onStart}
              data-testid="get-started-btn"
              className="w-full sm:w-auto btn-primary text-lg px-8 py-4 flex items-center justify-center gap-2 group animate-pulse-orange"
            >
              Get Your FlowGen
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={onStart}
              className="w-full sm:w-auto btn-secondary text-lg px-8 py-4"
            >
              Explore
            </button>
          </div>
          
          <p className="mt-8 text-sm text-brand-gray-500">No credit card required • Instant access</p>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="py-24 bg-brand-gray-950 border-t border-brand-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-headline uppercase tracking-wider text-white mb-4">
              Your AI Best Friend, Right in Your Flow
            </h2>
            <p className="text-brand-gray-400">A complete suite of tools to automate your social presence.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 rounded-2xl bg-black border border-brand-gray-800 hover:border-brand-orange/50 transition-all duration-300 group">
              <div className="w-12 h-12 bg-brand-orange/10 border border-brand-orange/30 rounded-lg flex items-center justify-center mb-6 group-hover:bg-brand-orange/20 transition-colors">
                <Layout className="w-6 h-6 text-brand-orange" />
              </div>
              <h3 className="text-lg font-headline uppercase tracking-wide text-white mb-3">Strategic Calendars</h3>
              <p className="text-brand-gray-400 text-sm leading-relaxed">
                Define your persona and get a 7-30 day content plan tailored to your specific audience and goals.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 rounded-2xl bg-black border border-brand-gray-800 hover:border-brand-orange/50 transition-all duration-300 group">
              <div className="w-12 h-12 bg-brand-orange/10 border border-brand-orange/30 rounded-lg flex items-center justify-center mb-6 group-hover:bg-brand-orange/20 transition-colors">
                <ImageIcon className="w-6 h-6 text-brand-orange" />
              </div>
              <h3 className="text-lg font-headline uppercase tracking-wide text-white mb-3">AI Image Studio</h3>
              <p className="text-brand-gray-400 text-sm leading-relaxed">
                Generate high-quality, royalty-free visuals for your posts instantly using AI.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 rounded-2xl bg-black border border-brand-gray-800 hover:border-brand-orange/50 transition-all duration-300 group">
              <div className="w-12 h-12 bg-brand-orange/10 border border-brand-orange/30 rounded-lg flex items-center justify-center mb-6 group-hover:bg-brand-orange/20 transition-colors">
                <Globe className="w-6 h-6 text-brand-orange" />
              </div>
              <h3 className="text-lg font-headline uppercase tracking-wide text-white mb-3">Trend Hunter</h3>
              <p className="text-brand-gray-400 text-sm leading-relaxed">
                Research real-time news and trending topics in your niche, making your content timely and relevant.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How it Works */}
      <div className="py-24 border-t border-brand-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-headline uppercase tracking-wider text-white mb-8">How It Works</h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-orange text-black flex items-center justify-center font-headline text-lg">1</div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Define Your Profile</h4>
                    <p className="text-brand-gray-400 text-sm">Tell us about your brand, upload your logo, and set your tone.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-orange text-black flex items-center justify-center font-headline text-lg">2</div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Generate Strategy</h4>
                    <p className="text-brand-gray-400 text-sm">Our AI researches your industry and creates a multi-platform content calendar.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-orange text-black flex items-center justify-center font-headline text-lg">3</div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Customize & Publish</h4>
                    <p className="text-brand-gray-400 text-sm">Edit posts, generate visuals, overlay your branding, and push to your social channels.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10">
                <button 
                  onClick={onStart}
                  className="btn-primary flex items-center gap-2"
                >
                  Start Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="relative">
              {/* Orange glow behind card */}
              <div className="absolute inset-0 bg-brand-orange/10 blur-3xl rounded-full"></div>
              
              <div className="relative bg-brand-gray-900 border border-brand-gray-700 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-brand-orange rounded-lg flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <div className="h-2 w-24 bg-brand-gray-700 rounded mb-1"></div>
                    <div className="h-2 w-16 bg-brand-gray-800 rounded"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-brand-gray-800 rounded"></div>
                  <div className="h-4 w-5/6 bg-brand-gray-800 rounded"></div>
                  <div className="h-4 w-4/6 bg-brand-gray-800 rounded"></div>
                </div>
                <div className="mt-6 h-48 bg-brand-gray-800 rounded-lg border border-brand-gray-700 flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-brand-gray-600" />
                </div>
                <div className="mt-4 flex gap-2">
                  <div className="flex-1 h-8 bg-brand-orange rounded-full"></div>
                  <div className="w-20 h-8 bg-brand-gray-700 rounded-full border border-brand-gray-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-brand-gray-950 border-t border-brand-gray-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Sparkles className="w-12 h-12 text-brand-orange mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-headline uppercase tracking-wider text-white mb-4">
            Ready to Transform Your Content?
          </h2>
          <p className="text-brand-gray-400 mb-8">
            Join thousands of creators who have automated their social presence with FlowGen.
          </p>
          <button 
            onClick={onStart}
            className="btn-primary text-lg px-10 py-4"
          >
            Get Started Free
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-brand-gray-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-6">
          {/* ArcheForge branding */}
          <div className="flex justify-center">
            <p className="text-sm font-headline tracking-wider">
              <span className="text-white">ArcheForge</span>
              <span className="text-white align-super text-[10px]">™</span>
              <span className="text-brand-gray-500"> — Built on </span>
              <a href="https://soulprintengine.ai" target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline">
                SoulPrintEngine.ai
              </a>
              <span className="text-brand-orange align-super text-[10px]">™</span>
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-brand-orange" />
              <span className="font-headline uppercase tracking-wider text-white">FlowGen.social</span>
            </div>
            <div className="text-brand-gray-500 text-sm">
              © {new Date().getFullYear()} FlowGen.social by Cool Geek Media
            </div>
            <div className="flex gap-6 text-sm font-medium text-brand-gray-400">
              <button onClick={onShowPrivacy} className="hover:text-brand-orange transition-colors" data-testid="landing-privacy-link">Privacy</button>
              <button onClick={onShowTerms} className="hover:text-brand-orange transition-colors" data-testid="landing-terms-link">Terms</button>
              <button onClick={onShowContact} className="hover:text-brand-orange transition-colors" data-testid="landing-contact-link">Contact</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
